import {
	screenDimension,
	type DiscoveryEventKey,
} from "@/app/actions/trackDiscoveryEventUtils";
import {
	getTrackingConsentSync,
	logConsentSkip,
} from "@repo/tracking/consent/lib/consent-client";

/**
 * Keepalive fetches are aborted when the document unloads. Chromium reports
 * that as `TypeError: Failed to fetch`, Safari as `Load failed`, Firefox as
 * a `NetworkError`, and some browsers as `AbortError`. Those are expected
 * and must not `console.error` — Datadog RUM captures console errors.
 */
export function isExpectedKeepaliveFailure(error: unknown): boolean {
	if (typeof DOMException !== "undefined" && error instanceof DOMException) {
		if (error.name === "AbortError") {
			return true;
		}
	}
	if (!(error instanceof Error)) {
		return false;
	}
	if (error.name === "AbortError") {
		return true;
	}
	const message = error.message;
	return (
		message === "Failed to fetch" ||
		message === "Load failed" ||
		message === "The user aborted a request." ||
		message.includes("NetworkError")
	);
}

/**
 * Fires a Discovery funnel event via `fetch` with `keepalive: true` rather
 * than `sendBeacon` — same delivery-through-unload guarantee, but a normal
 * request that browser extensions/network tooling can inspect and modify
 * (e.g. injecting a GTM Preview debug header). Use this instead of the
 * `trackDiscoveryEvent` Server Action for plain `<a href>` click handlers,
 * since a full-page nav aborts a fire-and-forget fetch before it completes.
 */
export function sendDiscoveryEventBeacon(
	event: DiscoveryEventKey,
	properties?: Record<string, unknown>,
	options?: {referrer?: string | null}
): void {
	if (typeof window === "undefined") {
		return;
	}
	if (!getTrackingConsentSync().analytics) {
		logConsentSkip(event);
		return;
	}

	const basePath = process.env.NEXT_PUBLIC_DISCOVERY_BASE_PATH ?? "";
	const referrer =
		options?.referrer !== undefined
			? options.referrer
			: document.referrer || null;
	const screenHeight = screenDimension(window.screen?.height);
	const screenWidth = screenDimension(window.screen?.width);
	// Fire-and-forget: the caller doesn't await this (a click handler can't
	// block on it), so the async work runs in a detached IIFE rather than
	// making this function itself `async` and leaving a floating promise.
	void (async () => {
		try {
			const response = await fetch(`${basePath}/api/track-discovery-event`, {
				method: "POST",
				keepalive: true,
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({
					event,
					properties,
					referrer,
					...(screenHeight !== undefined ? {screenHeight} : {}),
					...(screenWidth !== undefined ? {screenWidth} : {}),
				}),
			});
			if (!response.ok) {
				console.error("Discovery event beacon failed:", response.status);
			}
		} catch (error) {
			if (isExpectedKeepaliveFailure(error)) {
				return;
			}
			// Best-effort; a failed send shouldn't surface to the user.
			console.error("Discovery event beacon request failed:", error);
		}
	})();
}
